import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Search, Check, Info } from 'lucide-react';
import { api } from '../lib/api';

/**
 * Searchable multi-select combobox. Fetches options from `endpoint?q=` (GET).
 * Iter 69 — supports an optional `desc` field per option; renders a small
 * popup explanation when the user clicks the info icon. This is the
 * "popup reference" the clinician asked for when picking ICD-10 codes.
 *
 * props:
 *  - endpoint: string (e.g. "/lookup/icd10")
 *  - value: string[]  (selected codes)
 *  - onChange: (codes: string[]) => void
 *  - placeholder, label, testid
 */
export default function Combobox({ endpoint, value = [], onChange, placeholder = 'Search…', label, testid, disabled }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [opts, setOpts] = useState([]);
  const [all, setAll] = useState([]);
  const [popoverFor, setPopoverFor] = useState(null);  // code currently showing description
  const wrap = useRef(null);

  useEffect(() => {
    api.get(endpoint).then(r => { setAll(r.data); setOpts(r.data); }).catch(() => {});
  }, [endpoint]);

  useEffect(() => {
    const h = (e) => { if (wrap.current && !wrap.current.contains(e.target)) { setOpen(false); setPopoverFor(null); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const search = (val) => {
    setQ(val);
    const ql = val.trim().toLowerCase();
    if (!ql) { setOpts(all); return; }
    setOpts(all.filter(o =>
      o.code.toLowerCase().includes(ql) ||
      o.name.toLowerCase().includes(ql) ||
      (o.desc || '').toLowerCase().includes(ql)
    ));
  };

  const selectedItems = useMemo(
    () => all.filter(o => value?.includes(o.code)),
    [all, value]
  );

  const toggle = (code) => {
    if (disabled) return;
    const s = new Set(value || []);
    if (s.has(code)) s.delete(code); else s.add(code);
    onChange(Array.from(s));
  };

  return (
    <div className="relative" ref={wrap}>
      {label && <div className="text-xs text-fg-muted mb-1">{label}</div>}
      <div
        onClick={() => !disabled && setOpen(true)}
        className={`input min-h-[42px] flex flex-wrap gap-1.5 items-center cursor-text ${disabled?'opacity-60':''}`}
        data-testid={testid}
      >
        {selectedItems.map(it => (
          <span key={it.code} className="badge-neutral inline-flex items-center gap-1 text-[11px]" title={it.desc || ''}>
            <span className="font-mono font-semibold">{it.code}</span>
            <span className="max-w-[160px] truncate">{it.name}</span>
            <button type="button" className="print:hidden" onClick={(e)=>{ e.stopPropagation(); toggle(it.code); }}>
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        {(!selectedItems?.length) && <span className="text-fg-muted text-sm">{placeholder}</span>}
      </div>
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-soft rounded-lg shadow-xl max-h-72 overflow-auto">
          <div className="p-2 sticky top-0 bg-white border-b border-soft flex items-center gap-2">
            <Search className="w-4 h-4 text-fg-muted" />
            <input autoFocus className="flex-1 text-sm outline-none" placeholder="Type to filter (code, name, or description)"
                    value={q} onChange={e=>search(e.target.value)}
                    data-testid={`${testid}-search`} />
            <span className="text-[10px] text-fg-muted">{opts.length} match{opts.length === 1 ? '' : 'es'}</span>
          </div>
          {opts.map(o => {
            const selected = value?.includes(o.code);
            const showPopover = popoverFor === o.code;
            return (
              <div key={o.code}
                   className={`relative px-3 py-2 text-sm flex items-start gap-2 hover:bg-[#F2EBDB] ${selected?'bg-[#F2EBDB]':''}`}>
                <div onClick={()=>toggle(o.code)} className="flex items-start gap-2 flex-1 cursor-pointer"
                     data-testid={`${testid}-opt-${o.code}`}>
                  <div className="w-4 h-4 rounded border border-soft flex items-center justify-center mt-0.5 flex-shrink-0">
                    {selected && <Check className="w-3 h-3 text-brand" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono font-semibold text-fg">{o.code}</div>
                    <div className="text-xs text-fg-muted">{o.name}</div>
                  </div>
                </div>
                {o.desc && (
                  <button type="button"
                          onClick={(e) => { e.stopPropagation(); setPopoverFor(showPopover ? null : o.code); }}
                          className={`flex-shrink-0 p-1 rounded text-fg-muted hover:text-brand hover:bg-white ${showPopover ? 'bg-white text-brand' : ''}`}
                          aria-label="Show explanation"
                          data-testid={`${testid}-info-${o.code}`}>
                    <Info className="w-3.5 h-3.5"/>
                  </button>
                )}
                {showPopover && o.desc && (
                  <div className="absolute z-40 left-2 right-2 top-full mt-1 p-2.5 bg-[#0F1B2D] text-white text-xs rounded-md shadow-xl"
                       data-testid={`${testid}-desc-${o.code}`}>
                    <div className="font-mono font-bold text-[#FFB000] mb-1">{o.code} · {o.name}</div>
                    <div className="leading-relaxed">{o.desc}</div>
                  </div>
                )}
              </div>
            );
          })}
          {!opts.length && <div className="p-4 text-xs text-fg-muted text-center">No matches.</div>}
        </div>
      )}
    </div>
  );
}
